/**
 *  Copyright (c) 2022, 2024 Texas Instruments Incorporated
 *  All rights reserved.
 *
 *  Redistribution and use in source and binary forms, with or without
 *  modification, are permitted provided that the following conditions
 *  are met:
 *
 *  *   Redistributions of source code must retain the above copyright
 *  notice, this list of conditions and the following disclaimer.
 *  notice, this list of conditions and the following disclaimer in the
 *  documentation and/or other materials provided with the distribution.
 *  *   Neither the name of Texas Instruments Incorporated nor the names of
 *  its contributors may be used to endorse or promote products derived
 *  from this software without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 *  AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO,
 *  THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
 *  PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR
 *  CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 *  EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
 *  PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS;
 *  OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR
 *  OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE,
 *  EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import { Component, h, State, Element } from '@stencil/core';
import { ActionRegistry } from 'ti-guicomposer-components/@ti/gc-widget-menu/lib/ActionRegistry';
import { GcFiles } from 'ti-guicomposer-components/@ti/gc-core-assets/lib/GcFiles';
import { WidgetAnnouncement } from 'ti-guicomposer-components/types/gc-widget-announcement/gc-widget-announcement';

const CMD_SHOW_ABOUT = 'cmd_show_about';
const CMD_SHOW_SEARCH = 'cmd_show_search';
const CMD_SHOW_FAQ = 'cmd_show_faq';
const CMD_SHOW_GETTINGSTARTED = 'cmd_show_gettingstarted';

export interface CloudTool {
  id: string;
  name: string;
  icon: string;
  url: string;
  desc: string;
  learnMore?: string;
  about?: string;
  category: 'develop' | 'explore' | 'help';
  menu?: boolean;
  tile?: boolean;
  enabled: boolean;
  external?: boolean;
}

export interface DesktopTool {
  name: string;
  desc: string;
  icon?: string;
  url: string;
  category: 'configuration' | 'utilities' | 'optimization';
  productFamilyList: string[];
  hidden?: boolean;
}

@Component({
  tag: 'app-root',
  styleUrl: 'app-root.scss',
  shadow: true,
})
export class AppRoot {
  private announcementEl: WidgetAnnouncement;
  private tiHeaderHeight = 110;
  @State() cloudTools: CloudTool[] = [];
  @State() desktopTools: DesktopTool[] = [];
  @Element() el: HTMLElement;


  searchBarInputText: string = '';

  handleSearchInput(event) {
    this.searchBarInputText = event.target.value.trim();
  }

  handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.handleSearchQuerySubmit();
    }
  }

  handleSearchQuerySubmit() {
    if (this.searchBarInputText) {
      window.location.href = `/search?query=${encodeURIComponent(this.searchBarInputText)}`;
    }
  }

  render() {
    return [
      <div id="page_root">
        <gc-widget-theme hidden />

        <div id="ti-header-part" class="ti_header-top">
            <a class="ti_header-top-logo" href="//www.ti.com" data-navtitle="header_logo_link" aria-label="Home">
                <img alt="Home" src="//www.ti.com/etc/designs/ti/images/ui/ic-logo.svg"/>
            </a>

            <div class="ti_header-top-searchbox">
              {document.location.pathname == '/search' ? null : (
                <div id="search_bar_container">
                  <gc-widget-input
                    id="search_input_bar"
                    label={'Search'}
                    placeholder={'Search'}
                    selectOnFocus={'true'}
                    intermediateChanges={'true'}
                    onInput={event => this.handleSearchInput(event)}
                    onKeyPress={event => this.handleKeyPress(event)}
                  ></gc-widget-input>

                  <gc-widget-icon
                    id="search_icon_button"
                    backgorund={'white'}
                    appearance={'custom'}
                    icon={'filled:action:search'}
                    onClick={() => this.handleSearchQuerySubmit()}
                  ></gc-widget-icon>
                </div>
              )}
            </div>
            <gc-widget-authentication slot="tool-list" disableLogoutPrompt={true} />
        </div>

        <gc-widget-announcement id="announcement" ref={(el: WidgetAnnouncement) => (this.announcementEl = el)} updateInterval="10"></gc-widget-announcement>
        
        <div id="menu_parent">
          <gc-widget-menubar
            productName={'TI Developer Zone'}
            productIconFolderPath={'/portal/assets/icons/'}
            productIcon={'tidevtools_all:devtools_logo'}
            openOnHover
            layout
            horizontal
            onProduct-name-clicked={() => (window.location.href = '/')}
          >
            <gc-widget-menuitem label={'Tools'}>
              {[...this.cloudTools]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(app =>
                  app.menu && (app.category === 'explore' || app.category === 'develop') ? (
                    <gc-widget-menuaction actionId={`cmd_open_${app.id}`} label={app.name} iconFolderPath={'/portal/assets/icons/'} icon={`tidevtools_all:${app.icon}`} />
                  ) : undefined,
                )}
            </gc-widget-menuitem>

            <gc-widget-menuitem label={'Help'}>
              <gc-widget-menuaction actionId={CMD_SHOW_GETTINGSTARTED} label={'Getting started guide'} icon={'action:shop'} />
              <gc-widget-menuaction actionId={CMD_SHOW_FAQ} label={'FAQ'} icon={'action:help'} />
              {this.cloudTools.map(app =>
                app.menu && app.category === 'help' ? (
                  <gc-widget-menuaction actionId={`cmd_open_${app.id}`} label={app.name} iconFolderPath={'/portal/assets/icons/'} icon={`tidevtools_all:${app.icon}`} />
                ) : undefined,
              )}
              <gc-widget-menuaction actionId={CMD_SHOW_ABOUT} label={'About'} icon={'action:info'} />
            </gc-widget-menuitem>
          </gc-widget-menubar>
        </div>
        <div id="page_container" innerHTML={this.getActivePageComponentTag()} />
      </div>,
    ];
  }

  componentDidRender() {
    const activeComponent = this.getActiveComponent();
    const properties = this.getActivePageComponentProperties();
    if (properties) {
      properties.forEach(prop => (activeComponent[prop.property] = prop.value));
    }
    this.adjustAppOffset();
  }

  async componentWillLoad() {
    await Promise.all([this.initCloudTools(), this.initDesktopTools()]);
    this.initActions();
  }

  async componentDidLoad() {
    (this.announcementEl as unknown as HTMLElement).addEventListener('announcement-removed', this.adjustAppOffset);
    (this.announcementEl as unknown as HTMLElement).addEventListener('announcement-added', this.adjustAppOffset);

    window.addEventListener('resize', this.adjustAppOffset);
    
    this.adjustAppOffset();

    // display the TI footer
    const footer = document.querySelector<HTMLDivElement>('footer#tiResponsiveFooter');
    if (footer) footer.style.display = 'block';
  }

  private adjustAppOffset = async () => {

    if (this.announcementEl) {
      const rect = await this.announcementEl.getAnnouncementClientRect();
      if (rect != undefined) {
        const header =  (this.el.shadowRoot.querySelector('#ti-header-part') as HTMLDivElement);
        this.tiHeaderHeight = header.getBoundingClientRect().height;
        const offsetheight = rect.height + this.tiHeaderHeight; 
        header.style.marginTop = rect.height  + 'px';
        (this.el.shadowRoot.querySelector('#menu_parent') as HTMLDivElement).style.top = offsetheight + 'px';
      }
    } else {
      (this.el.shadowRoot.querySelector('#menu_parent') as HTMLDivElement).style.top = this.tiHeaderHeight + 'px';
    }
  };

  private getActiveComponent<T>() {
    return this.el.shadowRoot.querySelector('#page_container > *') as unknown as T;
  }

  private getActivePageComponentProperties() {
    switch (location.pathname) {
      case '/gettingstarted':
      case '/gettingstarted/':
        break;

      case '/search':
      case '/search/':
        break;

      case '/faq':
      case '/faq/':
        return [];

      case '/about':
      case '/about/':
        return [{ property: 'cloudTools', value: this.cloudTools }];

      case '/':
      case '/download':
      case '/download/':
        return [
          { property: 'cloudTools', value: this.cloudTools },
          { property: 'desktopTools', value: this.desktopTools },
        ];
    }
  }

  private getActivePageComponentTag() {
    switch (location.pathname) {
      case '/gettingstarted':
      case '/gettingstarted/':
        return '<app-getting-started />';
      case '/faq':
      case '/faq/':
        return '<app-faq />';
      case '/search':
      case '/search/':
        return '<app-search />';
      case '/about':
      case '/about/':
        return '<app-about />';
      default:
        return '<app-home />';
    }
  }

  private async initCloudTools() {
    this.cloudTools = (await GcFiles.readJsonFile('/portal/assets/json/cloud_tools.json')) as CloudTool[];
    const appIds = [];
    this.cloudTools.forEach(app => appIds.push(app.id));
    const response = await fetch('/filterServices', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appIds),
    });
    const availableCloudApps = (await response.json()) as string[];
    this.cloudTools.forEach(app => (app.external || availableCloudApps.includes(app.id) ? (app.enabled = true) : undefined));
  }

  private async initDesktopTools() {
    this.desktopTools = (await GcFiles.readJsonFile('/portal/assets/json/desktop_tools.json')) as DesktopTool[];
  }

  private initActions() {
    /* static actions */
    ActionRegistry.registerAction(CMD_SHOW_ABOUT, {
      run: () => (window.location.href = '/about'),
    });
    ActionRegistry.registerAction(CMD_SHOW_SEARCH, {
      run: () => (window.location.href = '/search'),
    });
    ActionRegistry.registerAction(CMD_SHOW_FAQ, {
      run: () => (window.location.href = '/faq'),
    });
    ActionRegistry.registerAction(CMD_SHOW_GETTINGSTARTED, {
      run: () => (window.location.href = '/gettingstarted'),
    });

    /* dynamic actions */
    this.cloudTools.forEach(app => {
      ActionRegistry.registerAction(`cmd_open_${app.id}`, {
        run: () => {
          window.ti.analytics.plOpenCloudTool(app.id, 'menu');
          window.open(app.url, app.id.replace('default/', ''));
        },
        isVisible: () => true,
        isEnabled: () => app.enabled,
      });
    });
  }
}
